/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import {
  CreditCard,
  Check,
  Copy,
  Radio,
  Wifi,
  Receipt,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RotateCcw,
  XCircle,
  Loader2,
  HelpCircle,
  ArrowLeft,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSettings from "@/hooks/useSettings";
import { toast } from "react-hot-toast";
import {
  markTerminalPaymentDoneApi,
  cancelTerminalPaymentApi,
} from "@/api/take-order/take-order";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * TerminalPaymentResponseDialog displays response details for terminal-based card payments in Reservations.
 * Implements a 4-step workflow:
 *  1. COUNTDOWN: Live timer (payment_alert_time) with progress bar
 *  2. STATUS_CHECK: Prompt "Is the payment complete in pax terminal?"
 *  3. CONFIRM_DONE: Verification card + "Confirm & Mark as Done" (calls markTerminalPaymentDoneApi)
 *  4. WAIT_OR_CANCEL: "Yes, wait for a while" (resets timer) or "No Transaction Processing, Cancel Request" (calls cancelTerminalPaymentApi)
 */
export default function TerminalPaymentResponseDialog({
  isOpen,
  onClose,
  responseData,
  reservation,
  onPaymentCompleted,
  onPaymentCancelled,
}) {
  const { settings } = useSettings();
  const hasPaxTerminal = settings?.has_pax_terminal === true;

  // Alert duration in seconds from settings (defaults to 30)
  const defaultAlertTime =
    Number(settings?.payment_alert_time) > 0
      ? Number(settings?.payment_alert_time)
      : 30;

  // State machine: 'COUNTDOWN' | 'STATUS_CHECK' | 'CONFIRM_DONE' | 'WAIT_OR_CANCEL'
  const [step, setStep] = useState("COUNTDOWN");
  const [timeLeft, setTimeLeft] = useState(defaultAlertTime);
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerRef = useRef(null);

  // Destructure response fields
  const status = responseData?.data?.status || responseData?.status || "dispatched";
  const deviceId =
    responseData?.data?.device_id ||
    responseData?.data?.dispatched_terminal_payments?.[0]?.device_id ||
    "N/A";
  const uuid =
    responseData?.data?.uuid ||
    responseData?.data?.dispatched_terminal_payments?.[0]?.uuid ||
    "";
  const paymentMethod = responseData?.data?.payment_method || "card";
  const currency =
    responseData?.data?.currency ||
    responseData?.data?.dispatched_terminal_payments?.[0]?.currency ||
    "GBP";
  const reference =
    responseData?.data?.reference ||
    `RES-${reservation?.id || reservation?.booking_id || ""}`;
  const message = responseData?.message || "Payment dispatched to the terminal.";

  // Calculate amount to display
  let displayAmount = "0.00";
  if (responseData?.data?.amount_minor) {
    displayAmount = (responseData.data.amount_minor / 100).toFixed(2);
  } else if (reservation?.advance) {
    displayAmount = Number(reservation.advance).toFixed(2);
  }

  // Handle Copy to Clipboard
  const handleCopy = () => {
    if (!uuid) return;
    navigator.clipboard.writeText(uuid);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currencySymbol = currency === "GBP" ? "£" : "$";

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep("COUNTDOWN");
      setTimeLeft(defaultAlertTime);
      setIsSubmitting(false);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [isOpen, defaultAlertTime, responseData]);

  // Countdown timer logic
  useEffect(() => {
    if (!isOpen || step !== "COUNTDOWN") {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setStep("STATUS_CHECK");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, step]);

  // Reset timer for "Yes, wait for a while"
  const handleWaitForAWhile = () => {
    setTimeLeft(defaultAlertTime);
    setStep("COUNTDOWN");
  };

  // Mark Terminal Payment as Done
  const handleMarkAsDone = async () => {
    if (!uuid) {
      toast.error("No terminal transaction UUID found to mark as done.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await markTerminalPaymentDoneApi(uuid);
      if (res?.status === "success" || res?.status === 200) {
        toast.success(res?.message || "Terminal payment confirmed and marked as done!");
        if (typeof onPaymentCompleted === "function") {
          onPaymentCompleted(res);
        }
        onClose();
      } else {
        toast.error(res?.message || "Failed to mark terminal payment as done.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Error confirming terminal payment."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel Terminal Payment Request
  const handleCancelRequest = async () => {
    if (!uuid) {
      toast.error("No terminal transaction UUID found to cancel.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await cancelTerminalPaymentApi(uuid);
      if (res?.status === "success" || res?.status === 200) {
        toast.success(res?.message || "Terminal payment request cancelled.");
        if (typeof onPaymentCancelled === "function") {
          onPaymentCancelled(res);
        }
        onClose();
      } else {
        toast.error(res?.message || "Failed to cancel terminal payment request.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Error cancelling terminal payment."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Progress percentage for countdown
  const progressPercent = Math.max(0, Math.min(100, (timeLeft / defaultAlertTime) * 100));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] bg-cblack4 border-gray-800 text-white overflow-hidden p-6 animate-fadeIn shadow-2xl">
        {/* ========================================================================= */}
        {/* STEP 1: COUNTDOWN                                                         */}
        {/* ========================================================================= */}
        {step === "COUNTDOWN" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-2.5 text-center">
              <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-cgreen/10 text-cgreen border border-cgreen/25 shadow-lg shadow-cgreen/10">
                <CreditCard className="h-7 w-7 z-10 animate-pulse" />
                <span className="absolute inset-0 rounded-full bg-cgreen/20 animate-ping opacity-75" />
                <Wifi className="absolute -top-1 -right-1 h-4 w-4 text-cgreen animate-pulse" />
              </div>

              <DialogTitle className="text-cgreen text-xl font-bold tracking-tight">
                Terminal Payment Dispatched
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[340px] leading-relaxed">
                {message}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-3.5 mt-2">
              {/* Prominent Amount Card */}
              <div className="bg-cblack3 p-4 rounded-xl border border-gray-800 text-center flex flex-col justify-center items-center shadow-inner relative overflow-hidden">
                <span className="text-[10px] text-cwhite3 uppercase tracking-wider font-semibold">
                  Advance Deposit Amount
                </span>
                <span className="text-cyellow text-3xl font-black font-sans mt-1 flex items-center justify-center gap-0.5 tracking-tight">
                  <span>{currencySymbol}</span>
                  <span>{displayAmount}</span>
                </span>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-cyellow/5 blur-2xl rounded-full pointer-events-none" />
              </div>

              {/* Alert Countdown Progress Card */}
              <div className="bg-gradient-to-br from-cblack3 to-cblack2 p-3.5 rounded-xl border border-cgreen/20 flex flex-col gap-2 relative shadow-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold text-cwhite2">
                    <Clock className="h-4 w-4 text-cgreen animate-spin-slow" />
                    <span>Awaiting Terminal Response</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-cgreen/15 text-cgreen px-2.5 py-0.5 rounded-full text-xs font-bold border border-cgreen/30">
                    <span>{timeLeft}s</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-cblack7 rounded-full h-2 overflow-hidden border border-gray-700/50">
                  <div
                    className="h-full bg-gradient-to-r from-cgreen via-emerald-400 to-teal-300 transition-all duration-1000 ease-linear rounded-full"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-[11px] text-cwhite3">
                  <span>Prompt customer on terminal</span>
                  <span>Alert in {timeLeft}s</span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-2.5 bg-cblack3 p-3 rounded-lg border border-gray-800/80 text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="text-cwhite3">Status:</span>
                  <span className="inline-flex items-center gap-1.5 w-fit px-2 py-0.5 rounded-full font-semibold bg-cgreen/15 text-cgreen border border-cgreen/30 text-[11px]">
                    <span className="w-1.5 h-1.5 rounded-full bg-cgreen animate-pulse" />
                    <span className="capitalize">{status}</span>
                  </span>
                </div>

                <div className="flex flex-col gap-0.5">
                  <span className="text-cwhite3">Terminal ID:</span>
                  <span className="font-semibold text-cwhite2 flex items-center gap-1">
                    <Radio className="h-3 w-3 text-cyellow shrink-0" />
                    Device #{deviceId}
                  </span>
                </div>

                <div className="flex flex-col gap-0.5 pt-1.5 border-t border-gray-800/50">
                  <span className="text-cwhite3">Payment Method:</span>
                  <span className="font-semibold text-cwhite2 capitalize">
                    {paymentMethod}
                  </span>
                </div>

                <div className="flex flex-col gap-0.5 pt-1.5 border-t border-gray-800/50">
                  <span className="text-cwhite3">Reference:</span>
                  <span className="font-semibold text-cwhite2 truncate">
                    {reference}
                  </span>
                </div>
              </div>

              {/* Monospace UUID box with Copy capability */}
              {uuid && (
                <div className="flex flex-col gap-1 bg-cblack3 p-2.5 rounded-lg border border-gray-800">
                  <span className="text-cwhite3 text-[11px]">Transaction UUID:</span>
                  <div className="flex items-center gap-2 bg-cblack4 p-1.5 rounded border border-gray-800">
                    <span className="font-mono text-[11px] text-cwhite2 break-all flex-1 select-all">
                      {uuid}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="p-1 rounded hover:bg-cblack7 text-cwhite3 hover:text-white transition shrink-0"
                      title="Copy UUID"
                    >
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-cgreen animate-bounce" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                className="w-full bg-cgreen hover:bg-cgreen/90 text-white font-bold py-2 rounded-lg transition-all"
                onClick={onClose}
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ========================================================================= */}
        {/* STEP 2: STATUS_CHECK (Triggered when countdown reaches 0s)                */}
        {/* ========================================================================= */}
        {step === "STATUS_CHECK" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-3 text-center">
              <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-cyellow/15 text-cyellow border border-cyellow/30 shadow-lg shadow-cyellow/10">
                <HelpCircle className="h-7 w-7 z-10 animate-bounce" />
                <span className="absolute inset-0 rounded-full bg-cyellow/20 animate-ping opacity-60" />
              </div>

              <DialogTitle className="text-white text-xl font-bold tracking-tight">
                Is the payment complete in PAX terminal?
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[340px] leading-relaxed">
                The alert countdown has finished. Please check whether the customer successfully paid on PAX terminal <strong>#{deviceId}</strong>.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-cblack3 p-4 rounded-xl border border-gray-800 flex items-center justify-between my-2">
              <div className="flex flex-col">
                <span className="text-[11px] text-cwhite3 uppercase tracking-wider font-semibold">
                  Deposit Amount
                </span>
                <span className="text-cyellow text-2xl font-black font-sans mt-0.5">
                  {currencySymbol}{displayAmount}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[11px] text-cwhite3">Reference</span>
                <span className="text-xs font-semibold text-cwhite2">{reference}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 mt-3">
              <Button
                type="button"
                onClick={() => setStep("CONFIRM_DONE")}
                className="w-full bg-cgreen hover:bg-cgreen/90 text-white font-bold py-3 text-sm rounded-xl shadow-lg shadow-cgreen/20 flex items-center justify-center gap-2 transition-all transform active:scale-98"
              >
                <CheckCircle2 className="h-4 w-4" />
                Yes, mark as done
              </Button>

              <Button
                type="button"
                onClick={() => setStep("WAIT_OR_CANCEL")}
                className="w-full bg-cblack7 hover:bg-cblack6 text-cwhite2 hover:text-white border border-gray-700 font-semibold py-3 text-sm rounded-xl flex items-center justify-center gap-2 transition-all"
              >
                <Clock className="h-4 w-4 text-cyellow" />
                No, Not done yet
              </Button>
            </div>
          </>
        )}

        {/* ========================================================================= */}
        {/* STEP 3: CONFIRM_DONE (Mark as done confirmation)                         */}
        {/* ========================================================================= */}
        {step === "CONFIRM_DONE" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-2.5 text-center">
              <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-cgreen/15 text-cgreen border border-cgreen/30 shadow-lg shadow-cgreen/10">
                <ShieldCheck className="h-7 w-7 z-10" />
                <span className="absolute inset-0 rounded-full bg-cgreen/20 animate-ping opacity-60" />
              </div>

              <DialogTitle className="text-cgreen text-xl font-bold tracking-tight">
                Confirm Terminal Payment
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[340px] leading-relaxed">
                Confirm that the transaction completed successfully in PAX terminal. This will update the reservation deposit status as <strong>PAID</strong>.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-gradient-to-br from-cblack3 to-cblack2 p-4 rounded-xl border border-cgreen/30 flex flex-col gap-3 my-2 shadow-inner">
              <div className="flex items-center justify-between pb-2 border-b border-gray-800">
                <span className="text-xs text-cwhite3">Amount Paid:</span>
                <span className="text-cyellow font-black text-xl">{currencySymbol}{displayAmount}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-cwhite3">Terminal Device:</span>
                <span className="font-semibold text-cwhite2">#{deviceId}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-cwhite3">Reservation Ref:</span>
                <span className="font-semibold text-cwhite2">{reference}</span>
              </div>
              {uuid && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-cwhite3">UUID:</span>
                  <span className="font-mono text-cwhite3 truncate max-w-[200px]">{uuid}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2.5 mt-3">
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={handleMarkAsDone}
                className="w-full bg-cgreen hover:bg-cgreen/90 text-white font-bold py-3 text-sm rounded-xl shadow-lg shadow-cgreen/20 flex items-center justify-center gap-2 transition-all"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Confirming Transaction...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Confirm & Mark as Done
                  </>
                )}
              </Button>

              <Button
                type="button"
                disabled={isSubmitting}
                onClick={() => setStep("STATUS_CHECK")}
                className="w-full bg-transparent hover:bg-cblack7 text-cwhite3 hover:text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Status Check
              </Button>
            </div>
          </>
        )}

        {/* ========================================================================= */}
        {/* STEP 4: WAIT_OR_CANCEL (Decision when payment is not done yet)           */}
        {/* ========================================================================= */}
        {step === "WAIT_OR_CANCEL" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-2.5 text-center">
              <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-cyellow/15 text-cyellow border border-cyellow/30 shadow-lg shadow-cyellow/10">
                <AlertTriangle className="h-7 w-7 z-10 text-cyellow" />
              </div>

              <DialogTitle className="text-white text-xl font-bold tracking-tight">
                Payment Still Pending
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[340px] leading-relaxed">
                Do you want to wait for more time to complete payment in PAX terminal, or cancel the terminal dispatch request if no transaction is processing?
              </DialogDescription>
            </DialogHeader>

            <div className="bg-cblack3 p-4 rounded-xl border border-gray-800 flex flex-col gap-2 my-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-cwhite3">Deposit Amount:</span>
                <span className="text-cyellow font-bold text-base">{currencySymbol}{displayAmount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-cwhite3">Terminal Device:</span>
                <span className="font-semibold text-cwhite2">#{deviceId}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2.5 mt-3">
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={handleWaitForAWhile}
                className="w-full bg-cblue hover:bg-cblue/90 text-white font-bold py-3 text-sm rounded-xl shadow-lg shadow-cblue/20 flex items-center justify-center gap-2 transition-all"
              >
                <RotateCcw className="h-4 w-4" />
                Yes, wait for a while
              </Button>

              <Button
                type="button"
                disabled={isSubmitting}
                onClick={handleCancelRequest}
                className="w-full bg-cred hover:bg-cred/90 text-white font-bold py-3 text-sm rounded-xl shadow-lg shadow-cred/20 flex items-center justify-center gap-2 transition-all"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cancelling Request...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    No Transaction Processing, Cancel Request
                  </>
                )}
              </Button>

              <Button
                type="button"
                disabled={isSubmitting}
                onClick={() => setStep("STATUS_CHECK")}
                className="w-full bg-transparent hover:bg-cblack7 text-cwhite3 hover:text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Status Check
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
